import { Trans, useLingui } from "@lingui/react/macro";
import type { SiteSummary } from "@rakazo/contracts";
import { Button, Skeleton } from "@rakazo/ui-web";
import { ExternalLink, Globe, History, Lock } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { siteHost } from "../lib/site-host";
import { readSite } from "../lib/site-read";

/** Resolve current authorized state; a message never supplies a trusted URL or access state.
 * Читаємо чинний дозволений стан; повідомлення не задає довірену адресу чи доступ. */
export function SiteResultCard({
  siteId,
  spaceId,
  refreshKey,
}: {
  siteId: string;
  spaceId: string;
  refreshKey?: number;
}) {
  const { t } = useLingui();
  const [state, setState] = useState<{
    key: string;
    site?: SiteSummary;
    failed?: boolean;
  } | null>(null);
  const [attempt, setAttempt] = useState(0);
  const key = JSON.stringify([spaceId, siteId, refreshKey]);
  const current = state?.key === key ? state : null;
  const previousKey = useRef(key);
  useEffect(() => {
    let ignore = false;
    // A new key means the message changed, so a read already in flight may predate the change.
    // Новий ключ означає зміну повідомлення, тож запит у польоті міг початися до неї.
    const fresh = previousKey.current !== key;
    previousKey.current = key;
    void readSite(spaceId, siteId, { fresh })
      .then((site) => {
        if (!ignore) setState({ key, site });
      })
      .catch(() => {
        if (!ignore) setState({ key, failed: true });
      });
    const refresh = () => setAttempt((value) => value + 1);
    window.addEventListener("focus", refresh);
    return () => {
      ignore = true;
      window.removeEventListener("focus", refresh);
    };
  }, [siteId, spaceId, key, attempt]);
  const site = current?.site;
  const library = `/app/sites?site=${encodeURIComponent(siteId)}&space=${encodeURIComponent(spaceId)}`;
  return (
    <article
      className="w-full max-w-md rounded-2xl bg-card p-3.5 shadow-sm ring-1 ring-border/50"
      aria-label={t`Site`}
      aria-busy={!current}
    >
      {!current ? (
        <div role="status" aria-label={t`Loading Sites`} className="space-y-3">
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
          <Skeleton className="h-10 w-28 rounded-full" />
        </div>
      ) : current.failed || !site ? (
        <div role="status" className="flex items-center justify-between gap-3">
          <span className="text-sm text-muted-foreground">
            <Trans>Site unavailable</Trans>
          </span>
          <Button
            variant="ghost"
            onClick={() => {
              setState(null);
              setAttempt((value) => value + 1);
            }}
          >
            <Trans>Retry</Trans>
          </Button>
        </div>
      ) : (
        <>
          <div className="flex items-start justify-between gap-3">
            <h3 className="min-w-0 break-words text-base font-medium tracking-tight">
              {site.title}
            </h3>
            <span className="flex shrink-0 items-center gap-1.5 pt-1 text-xs text-muted-foreground">
              {site.lifecycle !== "active" ? (
                <Trans>Unavailable</Trans>
              ) : site.visibility === "public" ? (
                <>
                  <Globe size={12} />
                  <Trans>Public</Trans>
                </>
              ) : (
                <>
                  <Lock size={12} />
                  <Trans>Private</Trans>
                </>
              )}
            </span>
          </div>
          <p className="mt-1 truncate text-xs text-muted-foreground" title={site.url}>
            {siteHost(site.url)}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {site.lifecycle === "active" && site.destinationReady && site.activeReleaseId ? (
              <a
                href={site.openUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-11 items-center gap-2 rounded-full bg-primary px-4 text-sm font-medium text-primary-foreground"
              >
                <Trans>Open</Trans>
                <ExternalLink size={14} />
              </a>
            ) : (
              <span className="me-auto text-xs text-muted-foreground">
                {site.lifecycle !== "active"
                  ? t`Unavailable`
                  : site.destinationReady
                    ? t`Draft`
                    : t`Address not ready`}
              </span>
            )}
            <Link
              to={library}
              className="inline-flex min-h-11 items-center gap-2 rounded-full px-3 text-sm text-muted-foreground hover:bg-muted"
            >
              <History size={16} />
              <Trans>Versions and access</Trans>
            </Link>
          </div>
        </>
      )}
    </article>
  );
}
